import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, MessageCircle, Smartphone, Send, Check, ChevronDown } from "lucide-react";
import { useToast } from "@rentos/ui";
import { api, ApiError } from "@/utils/api-client";
import { PageHero } from "../_components/page-hero";

const SUBJECTS = ["Product Demo", "General Question", "Partnership", "Bug Report"];
const FLEET_SIZES = ["1-10 vehicles", "11-50 vehicles", "50+ vehicles"];

// Pretty names for the plan slugs used in the pricing-page CTAs.
const PLAN_NAMES: Record<string, string> = {
  free: "Free",
  "starter-monthly": "Starter (monthly)",
  "growth-annual": "Growth (annual)",
};

function defaultSubject(type: string | null, plan: string | null): string {
  if (plan) return "Product Demo";
  if (type === "demo") return "Product Demo";
  return "";
}

// Prefill the message when a visitor arrives from a pricing-plan CTA.
function defaultMessage(plan: string | null): string {
  if (!plan) return "";
  const name = PLAN_NAMES[plan] ?? plan;
  return `Hi RentOS team, I'm interested in the ${name} plan and would like to get started. Here's a bit about my rental business:\n\n- Fleet type (bikes / scooters / cars): \n- Number of vehicles: \n- City: `;
}

type FormState = { name: string; email: string; phone: string; fleet: string; subject: string; message: string };
type Errors = Partial<Record<keyof FormState, string>>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Lenient phone check: optional +, digits/spaces/dashes/parens, 7–15 digits.
const PHONE_RE = /^[+]?[\d][\d\s()-]{6,18}$/;

function validate(form: FormState): Errors {
  const e: Errors = {};
  if (!form.name.trim()) e.name = "Please enter your name.";
  else if (form.name.trim().length < 2) e.name = "Name is too short.";

  if (!form.email.trim()) e.email = "Please enter your email.";
  else if (!EMAIL_RE.test(form.email.trim())) e.email = "Enter a valid email address.";

  if (form.phone.trim() && !PHONE_RE.test(form.phone.trim())) e.phone = "Enter a valid phone number.";

  if (!form.message.trim()) e.message = "Please tell us a little about your business.";
  else if (form.message.trim().length < 10) e.message = "Message should be at least 10 characters.";

  return e;
}

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();

  const planParam = searchParams.get("plan");
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    phone: "",
    fleet: "",
    subject: defaultSubject(searchParams.get("type"), planParam),
    message: defaultMessage(planParam),
  });
  const [errors, setErrors] = useState<Errors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormState, boolean>>>({});
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key: keyof FormState, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };
  const blur = (key: keyof FormState) => {
    setTouched((t) => ({ ...t, [key]: true }));
    setErrors(validate({ ...form }));
  };

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const nextErrors = validate(form);
    setErrors(nextErrors);
    setTouched({ name: true, email: true, phone: true, message: true });
    if (!consent) setConsentError(true);
    if (Object.keys(nextErrors).length > 0 || !consent) {
      showToast("Please fix the highlighted fields.", "error");
      return;
    }

    setLoading(true);
    try {
      await api.post("/api/public/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        fleetSize: form.fleet.trim(),
        subject: (form.subject || "General Question").trim(),
        message: form.message.trim(),
        consentConfirmed: true,
      });
      setSent(true);
      showToast("Sent! We will reply within a day.", "success");
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.message : "Could not send. Please try again.";
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <PageHero
        eyebrow="Talk to us"
        title={<>Talk about your <span className="gradient-text">rental workflow.</span></>}
        sub="Bookings, KYC, deposits or plan options — tell us what your business needs. We usually reply within one business day."
      />

      <section className="pb-24">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-5">
          {/* contact cards */}
          <aside className="space-y-3 lg:col-span-2">
            {[
              { icon: Mail, l: "Email", v: "support@rentos.tech", note: "Usually a reply within a day" },
              { icon: Smartphone, l: "Mobile", v: "Web, Android & iOS", note: "The same Business app" },
              { icon: MessageCircle, l: "Plans", v: "Free tier + paid modules", note: "Options based on fleet size" },
            ].map((c) => (
              <div key={c.l} className="flex items-start gap-4 rounded-2xl border border-border bg-surface/40 p-5">
                <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl border border-border bg-background/60 text-primary">
                  <c.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{c.l}</div>
                  <div className="mt-1 font-medium">{c.v}</div>
                  <div className="text-xs text-muted-foreground">{c.note}</div>
                </div>
              </div>
            ))}
            <div className="rounded-2xl border border-border bg-surface/40 p-5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Built for</div>
              <p className="mt-1 text-sm">Self-drive rentals · India</p>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                One owner per business, typically managing 5–50 bikes, scooters or cars.
              </p>
            </div>
          </aside>

          {/* form */}
          <div className="rounded-2xl border border-border bg-surface/40 p-6 sm:p-8 lg:col-span-3">
            {sent ? (
              <div className="grid place-items-center py-16 text-center">
                <div className="grid h-14 w-14 place-items-center rounded-full bg-primary/15 text-primary">
                  <Check className="h-7 w-7" />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold">Message sent.</h3>
                <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                  Thanks {form.name || "there"}. We got your message and will reply within one business day.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <h2 className="font-display text-xl font-semibold">Your enquiry</h2>
                <p className="mt-1 text-sm text-muted-foreground">Fields marked with * are required.</p>

                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Your name *" placeholder="e.g. Rohan Kale"
                    value={form.name} onChange={(v) => set("name", v)} onBlur={() => blur("name")}
                    error={touched.name ? errors.name : undefined} autoComplete="name"
                  />
                  <Field
                    label="Email *" type="email" placeholder="you@business.com"
                    value={form.email} onChange={(v) => set("email", v)} onBlur={() => blur("email")}
                    error={touched.email ? errors.email : undefined} autoComplete="email"
                  />
                  <Field
                    label="Phone" type="tel" placeholder="e.g. +91 98765 43210"
                    value={form.phone} onChange={(v) => set("phone", v)} onBlur={() => blur("phone")}
                    error={touched.phone ? errors.phone : undefined} autoComplete="tel"
                  />
                  <Dropdown
                    label="Fleet size" placeholder="Select fleet size"
                    value={form.fleet} options={FLEET_SIZES} onChange={(v) => set("fleet", v)}
                  />
                </div>

                <div className="mt-4">
                  <Dropdown
                    label="Topic" placeholder="What is this about?"
                    value={form.subject} options={SUBJECTS} onChange={(v) => set("subject", v)}
                  />
                </div>

                <label className="mt-4 block">
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Message *</span>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                    onBlur={() => blur("message")}
                    placeholder="Tell us about your business — fleet type, how you run bookings today, and what you'd like RentOS to help with."
                    className={`mt-1.5 w-full resize-none rounded-xl border bg-background/60 px-3.5 py-2.5 text-sm outline-none transition-colors focus:ring-2 focus:ring-primary/20 ${
                      touched.message && errors.message ? "border-destructive focus:border-destructive" : "border-border focus:border-primary/60"
                    }`}
                  />
                  {touched.message && errors.message && <p className="mt-1.5 text-xs text-destructive">{errors.message}</p>}
                </label>

                <label className="mt-4 flex items-start gap-2.5">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => { setConsent(e.target.checked); if (e.target.checked) setConsentError(false); }}
                    className="mt-0.5 h-4 w-4 rounded border-border accent-primary"
                  />
                  <span className="text-xs text-muted-foreground">
                    I agree to the{" "}
                    <a href="/legal?tab=privacy-policy" className="text-primary underline">privacy policy</a>.
                  </span>
                </label>
                {consentError && <p className="mt-1.5 text-xs text-destructive">Please agree to the privacy policy to continue.</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-glow disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? "Sending…" : "Send message"} <Send className="h-4 w-4" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

/* ── text input ─────────────────────────────────────────────────────────── */
function Field({
  label, value, onChange, onBlur, type = "text", placeholder, error, autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  type?: string;
  placeholder?: string;
  error?: string;
  autoComplete?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={`mt-1.5 w-full rounded-xl border bg-background/60 px-3.5 py-2.5 text-sm outline-none transition-colors focus:ring-2 focus:ring-primary/20 ${
          error ? "border-destructive focus:border-destructive" : "border-border focus:border-primary/60"
        }`}
      />
      {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
    </label>
  );
}

/* ── custom app-theme dropdown (no native <select>) ───────────────────────── */
function Dropdown({
  label, value, options, placeholder, onChange,
}: {
  label: string;
  value: string;
  options: string[];
  placeholder: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div className="block" ref={ref}>
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="relative mt-1.5">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`flex w-full items-center justify-between gap-2 rounded-xl border bg-background/60 px-3.5 py-2.5 text-left text-sm outline-none transition-colors focus:ring-2 focus:ring-primary/20 ${
            open ? "border-primary/60" : "border-border hover:border-primary/40"
          }`}
        >
          <span className={value ? "text-foreground" : "text-muted-foreground"}>{value || placeholder}</span>
          <ChevronDown className={`h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <ul
            role="listbox"
            className="glass absolute z-20 mt-2 max-h-60 w-full overflow-auto rounded-xl border border-border p-1 shadow-elegant"
          >
            {placeholder && (
              <li>
                <button
                  type="button"
                  onClick={() => { onChange(""); setOpen(false); }}
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-muted-foreground hover:bg-muted"
                >
                  {placeholder}
                </button>
              </li>
            )}
            {options.map((opt) => (
              <li key={opt}>
                <button
                  type="button"
                  role="option"
                  aria-selected={value === opt}
                  onClick={() => { onChange(opt); setOpen(false); }}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-muted ${
                    value === opt ? "bg-primary/10 text-foreground" : "text-foreground/90"
                  }`}
                >
                  {opt}
                  {value === opt && <Check className="h-4 w-4 text-primary" />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
